import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutFlex } from "uitk-react-layout-flex";

import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { TimeDropdown } from "components/shared/TimeDropdown/TimeDropdown";
import { SubmitButton } from "components/shared/SubmitButton/SubmitButton";
import { OffsetGridItem } from "components/shared/OffsetGridItem/OffsetGridItem";

import { CarsFormViewProps } from "../../typings";
import { locKeys } from "../l10n";
import { DifferentDropOffToggleButton } from "../DifferentDropOffToggle/DifferentDropOffToggleButton";
import { CarsHotwireHiddenSearchData } from "../HiddenSearchData/CarsHotwireHiddenSearchData";
import { useMultiLOBWizardRegion } from "components/flexComponents/WizardPWARegions/WizardRegionHotwire/WizardRegionContextProvider/MultiLOBWizardRegionContext";

export const CarsFormHotwireView: React.FunctionComponent<CarsFormViewProps> = observer((props) => {
  const {
    carWizardState,
    isDiffDropOff,
    onDifferentDropOffSelected,
    setIsDifferentDropOff,
    setInputsRefs,
    globalConfig,
    carConfig,
  } = props;

  const { pickUpTimeLabel, dropOffTimeLabel } = locKeys;
  const { timeFormats } = carWizardState.config;
  const format12hrConfig = timeFormats.timeFormat12Hour;
  const { isCarFormValid, updateInvalidFormsState } = carWizardState;

  const { isMultiLOB } = useMultiLOBWizardRegion();

  // Search date format YYYY-MM-DDTHH:mm:ss
  const formattedStartDate = `${carWizardState.date.start.toISOString().slice(0, 10)}T${carWizardState.pickupTime}:00`;
  const formattedEndDate = `${carWizardState.date.end.toISOString().slice(0, 10)}T${carWizardState.returnTime}:00`;

  const gridSpacing = isMultiLOB ? "four" : "three";
  const colSpacingForLarge = isMultiLOB ? 6 : 4;

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12, large: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12, large: colSpacingForLarge };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12, large: colSpacingForLarge };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: isMultiLOB ? 3 : 2 };
  const submitGridcolumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12, large: 12 };
  const submitColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12, large: 4 };
  if (isCarFormValid) {
    updateInvalidFormsState();
  }

  React.useEffect(() => {
    const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
    const { innerWidth: viewportSize } = window;

    if (viewportSize > VIEWPORT_MAX_SMALL_BREAKPOINT) {
      setIsDifferentDropOff(true);
    } else {
      setIsDifferentDropOff(false);
    }
  }, []);

  return (
    <>
      <UitkSpacing padding={{ blockstart: "three" }}>
        <UitkLayoutGrid columns={mainGridColumns} space={gridSpacing}>
          {!isCarFormValid && (
            <LobErrorSummary
              locHeadingToken={props.getLocError(false)!}
              locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
              locLinkToken={props.getLocError(true)}
              linkClickFocusId="id-roundtrip-error"
              inputErrorRef={carWizardState.errorInputRef}
              colSpan={mainGridColumns}
              setInputsRefs={setInputsRefs}
            />
          )}

          <Location
            setInputsRefs={setInputsRefs}
            originField
            destinationField={false}
            lobState={carWizardState}
            colSpan={locationColSpan}
            globalConfig={globalConfig}
          />
          <Viewport>
            <ViewMedium>
              <Location
                originField={false}
                destinationField
                lobState={carWizardState}
                colSpan={locationColSpan}
                globalConfig={globalConfig}
              />
            </ViewMedium>
          </Viewport>
        </UitkLayoutGrid>
      </UitkSpacing>

      <Viewport>
        <ViewSmall>
          <>
            <UitkSpacing margin={{ blockstart: "three" }}>
              <UitkLayoutGrid columns={mainGridColumns} space="three">
                {isDiffDropOff && (
                  <Location
                    originField={false}
                    destinationField
                    lobState={carWizardState}
                    colSpan={locationColSpan}
                    globalConfig={globalConfig}
                  />
                )}
              </UitkLayoutGrid>
            </UitkSpacing>
            <UitkLayoutFlex space="three">
              <DifferentDropOffToggleButton
                isDiffDropOff={isDiffDropOff}
                onDifferentDropOffSelected={onDifferentDropOffSelected}
                labelToken={carConfig.location.origin.differentDropOffToken}
              />
            </UitkLayoutFlex>
          </>
        </ViewSmall>
        <ViewMedium />
      </Viewport>

      <UitkSpacing margin={{ blockstart: "three" }}>
        <UitkLayoutGrid columns={mainGridColumns} space={gridSpacing}>
          <Dates
            lobState={carWizardState}
            colSpan={datesColSpan}
            singleDateSelect
            setInputsRefs={setInputsRefs}
            useHiddenInputs={false}
          />
          <TimeDropdown
            labelKey={pickUpTimeLabel}
            timeValues={carWizardState.config.timeValues}
            onChange={carWizardState.setPickupTime}
            value={carWizardState.pickupTime}
            invalidKey={carWizardState.timeStartInvalidKey}
            colSpan={timeDropdownColSpan}
            use12hr={format12hrConfig}
            showTimeIcon={false}
            dataTestId="pickUpTime"
          />
          <TimeDropdown
            labelKey={dropOffTimeLabel}
            timeValues={carWizardState.config.timeValues}
            onChange={carWizardState.setReturnTime}
            value={carWizardState.returnTime}
            colSpan={timeDropdownColSpan}
            use12hr={format12hrConfig}
            showTimeIcon={false}
            dataTestId="dropOffTime"
          />
        </UitkLayoutGrid>
      </UitkSpacing>
      <CarsHotwireHiddenSearchData formattedStartDate={formattedStartDate} formattedEndDate={formattedEndDate} />
      <UitkSpacing margin={{ blockstart: "three", blockend: "three" }}>
        <UitkLayoutGrid columns={submitGridcolumns} space={gridSpacing}>
          {isMultiLOB && <OffsetGridItem colSpan={{ large: 4 }} />}
          <SubmitButton lobState={carWizardState} colSpan={submitColSpan} globalConfig={globalConfig} />
        </UitkLayoutGrid>
      </UitkSpacing>
    </>
  );
});
